import { useState, useEffect, useCallback } from "react";
import { GraphView } from "./GraphView";
import { QueryPanel } from "./QueryPanel";
import { SourcesPanel } from "./SourcesPanel";
import { CollectionsPanel } from "./CollectionsPanel";

// ── Types ──────────────────────────────────────────────────────

export interface KnowledgeBase {
  id: string;
  name: string;
  description: string;
  source_count: number;
  created_at: string;
  updated_at: string;
}

export interface Source {
  id: string;
  source_type: "file" | "url" | "text" | "git";
  path: string;
  label: string;
  status: "pending" | "indexed" | "error" | "stale";
  chunk_count: number;
  last_indexed: string | null;
}

export interface KnowledgeNode {
  id: string;
  node_type: string;
  title: string;
  content: string;
  source_id: string | null;
  metadata: Record<string, string>;
  tags: string[];
  x: number;
  y: number;
}

export interface KnowledgeEdge {
  id: string;
  from_node: string;
  to_node: string;
  edge_type: string;
  weight: number;
}

export interface Collection {
  id: string;
  name: string;
  description: string;
  node_ids: string[];
  created_at: string;
}

// ── Tabs ───────────────────────────────────────────────────────

type Tab = "sources" | "graph" | "query" | "collections";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "sources", label: "Sources", icon: "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3" },
  { id: "graph", label: "Graph", icon: "M12 2a10 10 0 100 20 10 10 0 000-20z M12 8v8 M8 12h8" },
  { id: "query", label: "Query", icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" },
  { id: "collections", label: "Collections", icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" },
];

// ── Component ──────────────────────────────────────────────────

export function KnowledgeWorkbench() {
  const [bases, setBases] = useState<KnowledgeBase[]>([]);
  const [activeKb, setActiveKb] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("sources");
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [kbDetail, setKbDetail] = useState<any>(null);

  const loadBases = useCallback(() => {
    fetch("/api/knowledge")
      .then((r) => r.json())
      .then((d) => setBases(d.bases || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadBases();
  }, [loadBases]);

  // Load KB detail when active changes
  useEffect(() => {
    if (!activeKb) {
      setKbDetail(null);
      return;
    }
    fetch(`/api/knowledge/${activeKb}`)
      .then((r) => r.json())
      .then((d) => setKbDetail(d))
      .catch(() => {});
  }, [activeKb]);

  const handleCreate = useCallback(() => {
    if (!createName.trim()) return;
    fetch("/api/knowledge", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: createName, description: createDesc }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.ok) {
          setActiveKb(d.id);
          setCreateName("");
          setCreateDesc("");
          setShowCreate(false);
          loadBases();
        }
      })
      .catch(() => {});
  }, [createName, createDesc, loadBases]);

  const handleDelete = useCallback(
    (id: string) => {
      fetch(`/api/knowledge/${id}`, { method: "DELETE" })
        .then(() => {
          if (activeKb === id) setActiveKb(null);
          loadBases();
        })
        .catch(() => {});
    },
    [activeKb, loadBases],
  );

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div style={S.root}>
      {/* ── Left sidebar: KB list ── */}
      <div style={S.sidebar}>
        <div style={S.sidebarHeader}>
          <h3 style={S.heading}>Knowledge Bases</h3>
          <button style={S.addBtn} onClick={() => setShowCreate((v) => !v)}>
            {showCreate ? "\u2715" : "+"}
          </button>
        </div>

        {showCreate && (
          <div style={S.createForm}>
            <input
              style={S.input}
              placeholder="Knowledge base name..."
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              autoFocus
            />
            <input
              style={S.input}
              placeholder="Description (optional)"
              value={createDesc}
              onChange={(e) => setCreateDesc(e.target.value)}
            />
            <button
              style={{ ...S.submitBtn, opacity: createName.trim() ? 1 : 0.4 }}
              disabled={!createName.trim()}
              onClick={handleCreate}
            >
              Create
            </button>
          </div>
        )}

        <div style={S.kbList}>
          {bases.length === 0 && !showCreate && (
            <div style={S.empty}>
              No knowledge bases yet.
              <br />
              Create one to get started.
            </div>
          )}
          {bases.map((kb) => (
            <div
              key={kb.id}
              style={{
                ...S.kbItem,
                ...(activeKb === kb.id ? S.kbItemActive : {}),
              }}
              onClick={() => setActiveKb(kb.id)}
            >
              <div style={S.kbItemTop}>
                <span style={S.kbItemName}>{kb.name}</span>
                <button
                  style={S.kbDeleteBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(kb.id);
                  }}
                  title="Delete"
                >
                  \u2715
                </button>
              </div>
              <div style={S.kbItemMeta}>
                {kb.source_count} source{kb.source_count !== 1 ? "s" : ""} &middot;{" "}
                {fmtDate(kb.updated_at)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main area ── */}
      <div style={S.main}>
        {activeKb ? (
          <>
            {/* Tab bar */}
            <div style={S.tabBar}>
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  style={{
                    ...S.tab,
                    ...(activeTab === tab.id ? S.tabActive : {}),
                  }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: 14, height: 14 }}
                  >
                    <path d={tab.icon} />
                  </svg>
                  {tab.label}
                </button>
              ))}
              {kbDetail && (
                <div style={S.tabStats}>
                  {kbDetail.sources?.length || 0} sources &middot;{" "}
                  {kbDetail.name}
                </div>
              )}
            </div>

            {/* Tab content */}
            <div style={S.tabContent}>
              {activeTab === "sources" && (
                <SourcesPanel
                  kbId={activeKb}
                  sources={kbDetail?.sources || []}
                  onRefresh={() => {
                    fetch(`/api/knowledge/${activeKb}`)
                      .then((r) => r.json())
                      .then((d) => setKbDetail(d))
                      .catch(() => {});
                    loadBases();
                  }}
                />
              )}
              {activeTab === "graph" && <GraphView kbId={activeKb} />}
              {activeTab === "query" && <QueryPanel kbId={activeKb} />}
              {activeTab === "collections" && (
                <CollectionsPanel kbId={activeKb} />
              )}
            </div>
          </>
        ) : (
          <div style={S.placeholder}>
            <div style={S.placeholderIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 64, height: 64, opacity: 0.2 }}>
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="8" r="2" />
                <circle cx="7" cy="15" r="2" />
                <circle cx="17" cy="15" r="2" />
                <line x1="12" y1="10" x2="7" y2="13" />
                <line x1="12" y1="10" x2="17" y2="13" />
                <line x1="7" y1="17" x2="17" y2="17" />
              </svg>
            </div>
            <div style={S.placeholderTitle}>Knowledge Graph</div>
            <div style={S.placeholderDesc}>
              Build, explore, and query a knowledge base from your codebase, docs,
              conversations, and external sources. Select or create a knowledge
              base to get started.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────

const ACCENT = "var(--ot-accent, #80f3b2)";
const PURPLE = "var(--ot-purple, #bc8cff)";
const CYAN = "var(--ot-cyan, #7ae8e8)";
const BG = "var(--ot-bg-deep, #050608)";
const BG_SURFACE = "var(--ot-bg-surface, #0f1215)";
const BG_ELEVATED = "var(--ot-bg-elevated, #161b22)";
const TEXT = "var(--ot-text-primary, #e6edf3)";
const TEXT_DIM = "var(--ot-text-secondary, #8b949e)";
const TEXT_MUTED = "var(--ot-text-muted, #484f58)";
const BORDER = "var(--ot-border, rgba(255,255,255,0.06))";
const MONO = "var(--ot-font-mono, 'JetBrains Mono', monospace)";

const S: Record<string, React.CSSProperties> = {
  root: {
    display: "grid",
    gridTemplateColumns: "260px 1fr",
    height: "calc(100vh - 100px)",
    background: BG,
    color: TEXT,
    fontFamily: MONO,
    fontSize: "0.82rem",
  },

  // Sidebar
  sidebar: {
    display: "flex",
    flexDirection: "column",
    borderRight: `1px solid ${BORDER}`,
    background: BG_SURFACE,
    overflow: "hidden",
  },
  sidebarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.8rem 0.8rem 0.5rem",
    borderBottom: `1px solid ${BORDER}`,
  },
  heading: {
    margin: 0,
    fontSize: "0.8rem",
    fontWeight: 600,
    color: ACCENT,
    letterSpacing: "0.02em",
  },
  addBtn: {
    background: "transparent",
    border: `1px solid ${ACCENT}`,
    color: ACCENT,
    borderRadius: "4px",
    width: 26,
    height: 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85rem",
    fontFamily: MONO,
    cursor: "pointer",
  },
  createForm: {
    display: "flex",
    flexDirection: "column",
    gap: "0.35rem",
    padding: "0.6rem 0.8rem",
    borderBottom: `1px solid ${BORDER}`,
    background: BG_ELEVATED,
  },
  input: {
    background: "rgba(255,255,255,0.05)",
    border: `1px solid ${BORDER}`,
    borderRadius: "4px",
    color: TEXT,
    padding: "0.35rem 0.5rem",
    fontSize: "0.78rem",
    fontFamily: MONO,
    outline: "none",
  },
  submitBtn: {
    background: ACCENT,
    color: BG,
    border: "none",
    borderRadius: "4px",
    padding: "0.35rem 0.6rem",
    fontSize: "0.75rem",
    fontFamily: MONO,
    fontWeight: 600,
    cursor: "pointer",
  },
  kbList: {
    flex: 1,
    overflowY: "auto" as const,
  },
  empty: {
    padding: "1.5rem 0.8rem",
    color: TEXT_MUTED,
    textAlign: "center" as const,
    fontSize: "0.75rem",
    lineHeight: 1.6,
  },
  kbItem: {
    padding: "0.55rem 0.8rem",
    cursor: "pointer",
    borderBottom: `1px solid ${BORDER}`,
    transition: "background 0.1s",
  },
  kbItemActive: {
    background: "rgba(128,243,178,0.08)",
    borderLeft: `3px solid ${ACCENT}`,
  },
  kbItemTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  kbItemName: {
    fontWeight: 500,
    fontSize: "0.8rem",
  },
  kbDeleteBtn: {
    background: "transparent",
    border: "none",
    color: TEXT_MUTED,
    cursor: "pointer",
    fontSize: "0.7rem",
    fontFamily: MONO,
    padding: "0.1rem 0.3rem",
    borderRadius: "3px",
    opacity: 0.5,
  },
  kbItemMeta: {
    fontSize: "0.68rem",
    color: TEXT_DIM,
    marginTop: "0.15rem",
  },

  // Main area
  main: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  tabBar: {
    display: "flex",
    alignItems: "center",
    gap: "0.15rem",
    padding: "0.5rem 0.8rem",
    borderBottom: `1px solid ${BORDER}`,
    background: BG_SURFACE,
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: "0.35rem",
    padding: "0.4rem 0.7rem",
    background: "transparent",
    border: "none",
    borderRadius: "4px",
    color: TEXT_DIM,
    fontSize: "0.75rem",
    fontFamily: MONO,
    cursor: "pointer",
    transition: "all 0.1s",
  },
  tabActive: {
    background: "rgba(128,243,178,0.1)",
    color: ACCENT,
  },
  tabStats: {
    marginLeft: "auto",
    fontSize: "0.68rem",
    color: TEXT_MUTED,
  },
  tabContent: {
    flex: 1,
    overflow: "auto" as const,
  },

  // Placeholder
  placeholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: TEXT_DIM,
    gap: "0.8rem",
  },
  placeholderIcon: {
    marginBottom: "0.3rem",
  },
  placeholderTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: TEXT,
  },
  placeholderDesc: {
    fontSize: "0.78rem",
    maxWidth: "400px",
    textAlign: "center" as const,
    lineHeight: 1.6,
    color: TEXT_DIM,
  },
};
